var _templateObject;
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS, SIZES } from '@fupa/fupa-uikit';
import { AD_SLOT_HEIGHT, getAdContainerHeight } from 'app/components/ads/adHelpers';
import { useHydration } from 'app/hooks/useHydration';
import { AdSlotContainer } from 'app/components/ads/AdSlotContainer';
import { IS_CHROME, PARALLAX_AD_UNITS } from 'app/components/ads/ad.constants';
import { useNative } from 'app/contexts/NativeContext';
import { useRemountKey } from 'app/components/ads/useRemountKey';
import { useLayout } from 'app/contexts/LayoutContext';
import { LayoutMode } from 'app/styles/LayoutMode';
const slotsWithoutMargin = ['MatchEvent_1', 'Native_2'];
const SCAdSlotContainer = styled(AdSlotContainer).withConfig({
  componentId: "sc-1cu66dp-0"
})(["display:", ";background:", ";margin:", ";width:100%;z-index:0;justify-content:center;align-items:center;height:", ";overflow:hidden;", ";", ";", ";"], props => props.adLayout === 'desktop' ? 'none' : 'block', COLORS.white, props => props.margin, props => props.mobileHeight, props => props.isNative_1 && css(["max-width:", "px;"], props.theme.mobileContentWidth), props => props.setMinHeight && css(["min-height:", "px;"], props.setMinHeight), props => props.theme.desktopLayout(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    display: ", ";\n    height: ", ";\n    background: ", ";\n    ", ";\n  "])), props.adLayout === 'mobile' ? 'none' : 'block', props.desktopHeight, COLORS.white, props.isNative_1 && css(["height:100%;margin:0 0 0.75rem;border:1px solid ", ";border-radius:", ";max-width:", "px;"], COLORS.lightGrey, SIZES[4], props.theme.elementsWidth2Columns)));
// AdSlot: Component to set styles for wrapper where adSlot can be placed (SSR/CSR independent)
const AdSlot = _ref => {
  let {
    adUnit,
    limitedDim,
    adLayout,
    noMargin = false,
    ...props
  } = _ref;
  const {
    isNativeApp
  } = useNative();
  const isHydrating = useHydration();
  const layout = useLayout();
  const layoutChange = adUnit.includes('Skyscraper') ? layout === LayoutMode.DesktopWithSky : layout !== LayoutMode.Mobile;
  const disableSlotForNative = props.notOnNative && isNativeApp;
  const isDesktopLayoutWithAds = layoutChange && !adLayout || layoutChange && adLayout === 'desktop';
  const isMobileLayoutWithAds = !layoutChange && !adLayout || !layoutChange && adLayout === 'mobile';

  // use key to redefine adSlots after navigation from routeA to routeB has caused destroying all slots but adSlot get's not unmounted
  // e.g. AdSlot superbanner is placed globally by template and not by route
  const key = useRemountKey();
  const adSlotsHeight = getAdContainerHeight(limitedDim);
  let adHeight = adSlotsHeight[adUnit];
  let setMinHeight;
  let overwriteHeight;
  if (adUnit === 'Native_1' || adUnit === 'MatchEvent_1') {
    // don't set fix height based on defined dimensions for native_1 to avoid cutting of custom fupa advertorial template
    // use min-height and height: auto instead to display correctly
    overwriteHeight = 'auto';
    setMinHeight = adHeight.maxMobileHeight;
  }
  if (adUnit === 'Skyscraper' || adUnit === 'Skyscraper_2' || adUnit === 'Superbanner_2' || adUnit === 'Superbanner_3') {
    // Skyscraper: set min-height of ad container based max height of slot dimensions to enable 1x1 dimension to show creatives independent of 300x600
    // Superbanners: overwrite max-height to enable SoD slider desktop format
    setMinHeight = adHeight.maxDesktopHeight;
    overwriteHeight = 'auto';
  }

  // Disable Parallax for Chrome on Mobile because of wrong viewability measurement
  const parallaxSlots = IS_CHROME ? [] : PARALLAX_AD_UNITS;
  const isParallaxEnabledAdUnit = parallaxSlots.includes(adUnit);
  let mobileHeight = adHeight.maxMobileHeight + 'px';
  if (overwriteHeight) {
    mobileHeight = overwriteHeight;
  } else if (isParallaxEnabledAdUnit) {
    mobileHeight = "".concat(AD_SLOT_HEIGHT, "px");
  }
  const removeMargin = noMargin || slotsWithoutMargin.includes(adUnit);
  const desktopHeight = overwriteHeight ? overwriteHeight : adHeight.maxDesktopHeight + 'px';
  // Show AdPlaceholder with SCAdSlotContainer as div if
  // 1. mediaQueries don't match with adLayout if given (e.g. StreamPages)
  // 2. Hydration is not finished
  // 3. AdSlot is disabled for native app
  // 4. skipSky: AdSlot Superbanner is delivered with skin format
  return (isDesktopLayoutWithAds || isMobileLayoutWithAds) && !isHydrating && !disableSlotForNative && !props.skipSky ? /*#__PURE__*/React.createElement(SCAdSlotContainer, _extends({
    key: key,
    isNative_1: adUnit === 'Native_1',
    adLayout: adLayout,
    setMinHeight: setMinHeight,
    mobileHeight: mobileHeight,
    desktopHeight: desktopHeight,
    margin: removeMargin ? 0 : "".concat(SIZES['16'], " auto"),
    adUnit: adUnit,
    limitedDim: limitedDim
  }, props, {
    "data-sentry-element": "SCAdSlotContainer",
    "data-sentry-component": "AdSlot",
    "data-sentry-source-file": "AdSlot.tsx"
  })) : /*#__PURE__*/React.createElement(SCAdSlotContainer, _extends({
    as: "div",
    adLayout: adLayout,
    isNative_1: adUnit === 'Native_1',
    setMinHeight: setMinHeight,
    mobileHeight: mobileHeight,
    desktopHeight: desktopHeight,
    adUnit: adUnit,
    margin: removeMargin ? 0 : "".concat(SIZES['16'], " auto"),
    limitedDim: limitedDim
  }, props, {
    "data-sentry-element": "SCAdSlotContainer",
    "data-sentry-component": "AdSlot",
    "data-sentry-source-file": "AdSlot.tsx"
  }));
};
export { AdSlot };